import React, { useEffect, useState } from 'react';
import { Stack, Typography, CircularProgress } from '@mui/material';
import { CloudDone } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import 'dayjs/locale/ar';

dayjs.extend(relativeTime);

/**
 * Tiny "Saved" indicator. Reads the `dataUpdatedAt` timestamp from
 * the cart query and renders relative time. While a mutation is
 * in flight (`isFetching`), shows a spinner instead.
 */
const CartSavedIndicator = ({ updatedAt, isFetching }) => {
    const { i18n, t } = useTranslation();
    const isArabic = (i18n.language || 'en').startsWith('ar');

    // Tick every 30s so "saved 2 min ago" stays accurate.
    const [, setTick] = useState(0);
    useEffect(() => {
        const id = setInterval(() => setTick((n) => n + 1), 30_000);
        return () => clearInterval(id);
    }, []);

    if (!updatedAt && !isFetching) return null;

    if (isFetching) {
        return (
            <Stack direction="row" spacing={0.75} alignItems="center" sx={{ color: 'text.secondary', fontSize: 12 }}>
                <CircularProgress size={12} thickness={5} />
                <Typography variant="caption">
                    {t('cart.saving', 'Saving...')}
                </Typography>
            </Stack>
        );
    }

    const ago = dayjs(updatedAt).locale(isArabic ? 'ar' : 'en').fromNow();
    return (
        <Stack direction="row" spacing={0.75} alignItems="center" sx={{ color: 'success.main', fontSize: 12 }}>
            <CloudDone sx={{ fontSize: 16 }} />
            <Typography variant="caption">
                {t('cart.savedAt', { defaultValue: 'Saved {{ago}}', ago })}
            </Typography>
        </Stack>
    );
};

export default CartSavedIndicator;
